/**
 * Client-side validation utilities for forms and data integrity
 */

import React from 'react';
import { parseAmountInput } from './decimal';

/**
 * Validation error class
 */
export class ValidationError extends Error {
  constructor(message, field = null, code = null) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
    this.code = code;
  }
}

/**
 * Validation result class
 */
export class ValidationResult {
  constructor() {
    this.isValid = true;
    this.errors = {};
    this.warnings = {};
  }

  addError(field, message, code = null) {
    this.isValid = false;
    if (!this.errors[field]) {
      this.errors[field] = [];
    }
    this.errors[field].push({ message, code });
  }

  addWarning(field, message, code = null) {
    if (!this.warnings[field]) {
      this.warnings[field] = [];
    }
    this.warnings[field].push({ message, code });
  }

  getFieldErrors(field) {
    return this.errors[field] || [];
  }

  getFieldWarnings(field) {
    return this.warnings[field] || [];
  }

  hasFieldError(field) {
    return !!(this.errors[field] && this.errors[field].length > 0);
  }

  getAllErrors() {
    return Object.keys(this.errors).reduce((acc, field) => {
      acc[field] = this.errors[field].map(e => e.message);
      return acc;
    }, {});
  }
}

/**
 * Core validation functions
 */
export const validators = {
  required: (value, message = 'This field is required') => {
    if (value === null || value === undefined || value === '' || 
        (Array.isArray(value) && value.length === 0)) {
      throw new ValidationError(message, null, 'REQUIRED');
    }
    return true;
  },

  string: (value, options = {}) => {
    const { minLength, maxLength, pattern, message } = options;
    
    if (typeof value !== 'string') {
      throw new ValidationError(message || 'Must be a string', null, 'INVALID_TYPE');
    }

    if (minLength !== undefined && value.length < minLength) {
      throw new ValidationError(
        message || `Must be at least ${minLength} characters long`,
        null,
        'MIN_LENGTH'
      );
    }

    if (maxLength !== undefined && value.length > maxLength) {
      throw new ValidationError(
        message || `Must be at most ${maxLength} characters long`,
        null,
        'MAX_LENGTH'
      );
    }

    if (pattern && !pattern.test(value)) {
      throw new ValidationError(
        message || 'Invalid format',
        null,
        'INVALID_FORMAT'
      );
    }

    return true;
  },

  number: (value, options = {}) => {
    const { min, max, integer, positive, message } = options;
    
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    if (typeof numValue !== 'number' || isNaN(numValue)) {
      throw new ValidationError(message || 'Must be a valid number', null, 'INVALID_NUMBER');
    }

    if (integer && !Number.isInteger(numValue)) {
      throw new ValidationError(message || 'Must be a whole number', null, 'NOT_INTEGER');
    }

    if (positive && numValue <= 0) {
      throw new ValidationError(message || 'Must be a positive number', null, 'NOT_POSITIVE');
    }

    if (min !== undefined && numValue < min) {
      throw new ValidationError(
        message || `Must be at least ${min}`,
        null,
        'MIN_VALUE'
      );
    }

    if (max !== undefined && numValue > max) {
      throw new ValidationError(
        message || `Must be at most ${max}`,
        null,
        'MAX_VALUE'
      );
    }

    return true;
  },

  date: (value, options = {}) => {
    const { minDate, maxDate, message } = options;
    
    let dateValue;
    if (typeof value === 'string') {
      // Validate YYYY-MM-DD format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        throw new ValidationError(
          message || 'Date must be in YYYY-MM-DD format',
          null,
          'INVALID_DATE_FORMAT'
        );
      }
      dateValue = new Date(value + 'T00:00:00');
    } else if (value instanceof Date) {
      dateValue = value;
    } else {
      throw new ValidationError(message || 'Invalid date', null, 'INVALID_DATE');
    }

    if (isNaN(dateValue.getTime())) {
      throw new ValidationError(message || 'Invalid date', null, 'INVALID_DATE');
    }

    if (minDate && dateValue < minDate) {
      throw new ValidationError(
        message || `Date must be after ${minDate.toLocaleDateString()}`,
        null,
        'DATE_TOO_EARLY'
      );
    }

    if (maxDate && dateValue > maxDate) {
      throw new ValidationError(
        message || `Date must be before ${maxDate.toLocaleDateString()}`,
        null,
        'DATE_TOO_LATE'
      );
    }

    return true;
  },

  currency: (value, options = {}) => {
    const { allowNegative = false, maxAmount = 999999999.99, message } = options;

    const numValue = typeof value === 'string' ? parseAmountInput(value) : value;
    
    if (typeof numValue !== 'number' || isNaN(numValue)) {
      throw new ValidationError(message || 'Must be a valid amount', null, 'INVALID_CURRENCY');
    }

    if (!allowNegative && numValue < 0) {
      throw new ValidationError(message || 'Amount cannot be negative', null, 'NEGATIVE_AMOUNT');
    }

    if (Math.abs(numValue) > maxAmount) {
      throw new ValidationError(
        message || `Amount cannot exceed ${maxAmount.toLocaleString()}`,
        null,
        'AMOUNT_TOO_LARGE'
      );
    }

    // Check for reasonable decimal precision (2 decimal places for currency)
    const decimalPlaces = (numValue.toString().split('.')[1] || '').length;
    if (decimalPlaces > 2) {
      throw new ValidationError(
        message || 'Amount cannot have more than 2 decimal places',
        null,
        'TOO_MANY_DECIMALS'
      );
    }

    return true;
  },

  payee: (value, options = {}) => {
    const { message } = options;
    
    validators.required(value, message || 'Payee is required');
    validators.string(value, {
      minLength: 1,
      maxLength: 100,
      message: message || 'Payee name must be between 1 and 100 characters'
    });

    // Check for suspicious patterns
    if (/^\s+$/.test(value)) {
      throw new ValidationError(
        message || 'Payee name cannot be only whitespace',
        null,
        'WHITESPACE_ONLY'
      );
    }

    return true;
  }
};

/**
 * Budget validation schema
 */
export const budgetValidation = {
  name: (value) => {
    validators.required(value, 'Budget name is required');
    validators.string(value, {
      minLength: 1,
      maxLength: 100,
      message: 'Budget name must be between 1 and 100 characters'
    });
    
    // Check for duplicate names (this would need to be checked against existing budgets)
    if (/^\s+$/.test(value)) {
      throw new ValidationError('Budget name cannot be only whitespace', null, 'WHITESPACE_ONLY');
    }
    
    return true;
  },

  type: (value) => {
    validators.number(value, {
      integer: true,
      min: 0,
      max: 10,
      message: 'Budget type must be a valid integer between 0 and 10'
    });
    return true;
  }
};

/**
 * Ledger validation schema
 */
export const ledgerValidation = {
  name: (value) => {
    validators.required(value, 'Ledger name is required');
    validators.string(value, {
      minLength: 1,
      maxLength: 100,
      message: 'Ledger name must be between 1 and 100 characters'
    });
    
    if (/^\s+$/.test(value)) {
      throw new ValidationError('Ledger name cannot be only whitespace', null, 'WHITESPACE_ONLY');
    }
    
    return true;
  },

  type: (value) => {
    const validTypes = [0, 1, 2, 3, 4]; // Checking, Savings, Credit, Investment, Cash
    if (!validTypes.includes(parseInt(value))) {
      throw new ValidationError('Invalid ledger type', null, 'INVALID_LEDGER_TYPE');
    }
    return true;
  }
};

/**
 * Transaction validation schema
 */
export const transactionValidation = {
  date: (value) => {
    validators.required(value, 'Date is required');
    validators.date(value, {
      minDate: new Date('2000-01-01'),
      maxDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year in future
      message: 'Date must be between 2000 and one year from now'
    });
    return true;
  },

  payee: (value) => {
    validators.payee(value);
    return true;
  },

  amount: (debit, credit) => {
    // Exactly one of debit or credit must be provided
    const hasDebit = debit !== null && debit !== undefined && debit !== '' && parseAmountInput(debit) !== 0;
    const hasCredit = credit !== null && credit !== undefined && credit !== '' && parseAmountInput(credit) !== 0;
    
    if (!hasDebit && !hasCredit) {
      throw new ValidationError('Either debit or credit amount is required', null, 'NO_AMOUNT');
    }
    
    if (hasDebit && hasCredit) {
      throw new ValidationError('Cannot have both debit and credit amounts', null, 'BOTH_AMOUNTS');
    }
    
    const amount = hasDebit ? debit : credit;
    validators.currency(amount, {
      allowNegative: false,
      message: 'Amount must be a valid positive currency value'
    });
    
    return true;
  },

  budgetId: (value) => {
    if (value !== null && value !== undefined && value !== '') {
      validators.number(value, {
        integer: true,
        min: 0,
        message: 'Budget ID must be a valid integer'
      });
    }
    return true;
  }
};

/**
 * Validate an object against a schema
 */
export const validateObject = (data, schema) => {
  const result = new ValidationResult();
  
  for (const [field, validator] of Object.entries(schema)) {
    try {
      if (typeof validator === 'function') {
        validator(data[field]);
      } else if (Array.isArray(validator)) {
        // Multiple validators for the same field
        for (const v of validator) {
          v(data[field]);
        }
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        result.addError(field, error.message, error.code);
      } else {
        result.addError(field, 'Validation failed', 'UNKNOWN_ERROR');
      }
    }
  }
  
  return result;
};

/**
 * Validate budget data
 */
export const validateBudget = (budgetData) => {
  return validateObject(budgetData, budgetValidation);
};

/**
 * Validate ledger data
 */
export const validateLedger = (ledgerData) => {
  return validateObject(ledgerData, ledgerValidation);
};

/**
 * Validate transaction data
 */
export const validateTransaction = (transactionData) => {
  const result = new ValidationResult();
  
  // Validate individual fields
  try {
    transactionValidation.date(transactionData.date);
  } catch (error) {
    if (error instanceof ValidationError) {
      result.addError('date', error.message, error.code);
    }
  }
  
  try {
    transactionValidation.payee(transactionData.payee);
  } catch (error) {
    if (error instanceof ValidationError) {
      result.addError('payee', error.message, error.code);
    }
  }
  
  try {
    transactionValidation.amount(transactionData.debit, transactionData.credit);
  } catch (error) {
    if (error instanceof ValidationError) {
      result.addError('amount', error.message, error.code);
    }
  }
  
  try {
    transactionValidation.budgetId(transactionData.budgetId);
  } catch (error) {
    if (error instanceof ValidationError) {
      result.addError('budgetId', error.message, error.code);
    }
  }
  
  return result;
};

/**
 * Real-time validation hook for forms
 */
export const useFormValidation = (initialData, validationSchema) => {
  const [data, setData] = React.useState(initialData);
  const [errors, setErrors] = React.useState({});
  const [touched, setTouched] = React.useState({});
  
  const validateField = (field, value) => {
    try {
      if (validationSchema[field]) {
        validationSchema[field](value);
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        setErrors(prev => ({
          ...prev,
          [field]: error.message
        }));
      }
    }
  };
  
  const updateField = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
    if (touched[field]) {
      validateField(field, value);
    }
  };
  
  const touchField = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, data[field]);
  };
  
  const validateAll = () => {
    const result = validateObject(data, validationSchema);
    setErrors(result.getAllErrors());
    return result.isValid;
  };
  
  return {
    data,
    errors,
    touched,
    updateField,
    touchField,
    validateAll,
    isValid: Object.keys(errors).length === 0
  };
};

export default {
  validators,
  ValidationError,
  ValidationResult,
  validateBudget,
  validateLedger,
  validateTransaction,
  validateObject,
  useFormValidation,
};